import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

/**
 * RemoteJS Controller
 *
 * Replaces Rails UJS `remote: true` for links and forms that need to fetch and execute JS responses.
 * This is the Stimulus/Turbo-compatible way to make AJAX requests that return JavaScript.
 *
 * Usage for links:
 *   = link_to "Download", some_path,
 *       data: { controller: "remote-js", action: "click->remote-js#fetch" }
 *
 * Usage for forms:
 *   = form_tag some_path, data: { controller: "remote-js", action: "submit->remote-js#submit" } do
 *     ...
 *
 * With custom options:
 *   data: {
 *     controller: "remote-js",
 *     action: "click->remote-js#fetch",
 *     remote_js_method_value: "post",
 *     remote_js_confirm_value: "Are you sure?"
 *   }
 */
export default class extends Controller {
  static values = {
    method: { type: String, default: "get" },
    confirm: String,
    track: String,      // Umami event name (e.g., "File Downloaded")
    trackRole: String   // Umami event role (e.g., "admin")
  }

  /**
   * On connect, disable Turbo for this element to prevent:
   * 1. Prefetch on hover (would return 406 for JS-only endpoints)
   * 2. Turbo intercepting the click and navigating on error
   */
  connect() {
    // Disable Turbo prefetch - prevents HTML prefetch requests
    this.element.setAttribute("data-turbo-prefetch", "false")
    // Disable Turbo entirely for this link - we handle it ourselves via fetch
    this.element.setAttribute("data-turbo", "false")
  }

  /**
   * Handle link clicks - fetches URL from href
   */
  async fetch(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasConfirmValue && !window.confirm(this.confirmValue)) {
      return
    }

    const url = this.element.href || this.element.dataset.url
    if (!url) {
      console.error("remote-js: No URL found on element")
      return
    }

    // Track with Umami if configured (do this manually since we prevent default navigation)
    this.trackUmamiEvent()

    await this.executeRequest(url, this.methodValue.toUpperCase())
  }

  /**
   * Track Umami event if configured via Stimulus values
   * Uses data-remote-js-track-value instead of data-umami-event to avoid
   * Umami's automatic click handler (which uses capture phase and navigates)
   */
  trackUmamiEvent() {
    if (this.hasTrackValue && typeof window.umami !== "undefined") {
      const eventData = this.hasTrackRoleValue ? { role: this.trackRoleValue } : undefined
      window.umami.track(this.trackValue, eventData)
    }
  }

  /**
   * Handle form submissions - fetches URL from action, includes form data
   */
  async submit(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasConfirmValue && !window.confirm(this.confirmValue)) {
      return
    }

    const form = this.element
    const url = form.action
    const method = (form.method || "post").toUpperCase()
    const formData = new FormData(form)

    await this.executeRequest(url, method, formData)
  }

  /**
   * Execute the AJAX request and handle JS response
   */
  async executeRequest(url, method, formData = null) {
    try {
      const options = {
        method: method,
        headers: {
          "Accept": "text/javascript, application/javascript, */*",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin"
      }

      // Add form data for POST/PUT/PATCH requests
      if (formData && method !== "GET") {
        options.body = formData
      }

      logger.log("remote-js: Fetching", url, method)

      const response = await fetch(url, options)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get("content-type") || ""

      if (contentType.includes("javascript")) {
        const script = await response.text()
        logger.log("remote-js: Executing JS response")
        // Execute the JavaScript response
        eval(script)
      } else {
        console.warn("remote-js: Response was not JavaScript:", contentType)
      }
    } catch (error) {
      console.error("remote-js: Request failed:", error)
      this.showError()
    }
  }

  showError() {
    const flashContainer = document.getElementById("flash-container")
    if (flashContainer) {
      flashContainer.insertAdjacentHTML(
        "beforeend",
        `<div class="alert alert-danger alert-dismissible fade show" role="alert">
          שגיאה בביצוע הפעולה. אנא נסה שוב.
          <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>`
      )
    }
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
